import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchProducts, fetchCategories } from './api';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Button,
  IconButton,
  useTheme,
  Modal,
  Backdrop,
  Fade,
  Chip,
  Skeleton,
  useMediaQuery,
  Snackbar,
  Alert,
  Tooltip,
  Stack,
  Divider
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { getPriceInCurrency, getCurrencyDecimals, calculateDiscountPercentage } from './currencyHelpers';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { addFavorite, removeFavorite, getFavoriteIds } from './favoritesApi';
import config from './config';
import { useTranslation } from 'react-i18next';

// ✨ Animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const slideIn = keyframes`
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const heartBeat = keyframes`
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.3); }
  50% { transform: scale(1.1); }
  75% { transform: scale(1.2); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideTransition = keyframes`
  0% { opacity: 0; transform: translateX(30px); }
  100% { opacity: 1; transform: translateX(0); }
`;

const getImageUrls = (imageData) => {
  const fallbackUrl = '/logo-storesye.png';
  
  if (!imageData) {
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }
  
  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (imageData.thumbnail && imageData.medium && imageData.large) {
      return {
        thumbnail: imageData.thumbnail.startsWith('http') 
          ? imageData.thumbnail 
          : `${config.SITE_BASE_URL}${imageData.thumbnail}`,
        medium: imageData.medium.startsWith('http') 
          ? imageData.medium 
          : `${config.SITE_BASE_URL}${imageData.medium}`,
        large: imageData.large.startsWith('http') 
          ? imageData.large 
          : `${config.SITE_BASE_URL}${imageData.large}`
      };
    }
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }
  
  if (typeof imageData === 'string') {
    let fullUrl;
    if (imageData.startsWith('http')) {
      fullUrl = imageData;
    } else if (imageData.startsWith('data:image')) {
      fullUrl = imageData;
    } else if (imageData.startsWith('/images/')) {
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    } else if (imageData.startsWith('/')) {
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    } else {
      fullUrl = imageData;
    }
    return { thumbnail: fullUrl, medium: fullUrl, large: fullUrl };
  }
  
  return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
};

function GroupedProducts() {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [autoSlideInterval, setAutoSlideInterval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const { selectedBranch } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();

  // ✅ Open external product URL helper
  const handleOpenExternalProduct = (prod, e) => {
    if (e) e.stopPropagation();
    const productId = prod.Id || prod.id || prod.ProductId;
    const url = `https://www.elorayem.com/product/${productId}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    loadData();
  }, [selectedBranch, selectedCurrency]);

  useEffect(() => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      
      if (images.length > 1) {
        const interval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        
        setAutoSlideInterval(interval);
        
        return () => {
          clearInterval(interval);
        };
      }
    } else {
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        setAutoSlideInterval(null);
      }
    }
  }, [selectedProduct]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.all([
        fetchProducts(selectedBranch?.id, selectedCurrency),
        fetchCategories(selectedBranch?.id)
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
      await loadFavorites();
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar(t('errors.loadingFailed', 'Failed to load products'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const ids = await getFavoriteIds(user.Id || user.id, selectedBranch?.id);
        setFavoriteIds(ids);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const toggleFavorite = async (productId, productName, e) => {
    e?.stopPropagation();
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        showSnackbar(t('auth.loginRequired', 'Please login first'), 'warning');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.Id || user.id;

      if (favoriteIds.includes(productId)) {
        await removeFavorite(userId, productId, selectedBranch?.id);
        setFavoriteIds(prev => prev.filter(id => id !== productId));
        showSnackbar(t('favorites.removed', 'Removed from favorites'), 'info');
      } else {
        await addFavorite(userId, productId, selectedBranch?.id);
        setFavoriteIds(prev => [...prev, productId]);
        showSnackbar(t('favorites.added', 'Added to favorites'), 'success');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showSnackbar(t('errors.favoriteError', 'Could not update favorites'), 'error');
    }
  };

  const handleProductClick = (prod) => {
    setSelectedProduct(prod);
    setCurrentImageIndex(0);
  };

  const handleImageClick = useCallback((imageUrl, e) => {
    e?.stopPropagation();
    setSelectedImage(imageUrl);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const handleCloseProductModal = () => {
    setSelectedProduct(null);
    setCurrentImageIndex(0);
    if (autoSlideInterval) {
      clearInterval(autoSlideInterval);
      setAutoSlideInterval(null);
    }
  };

  const getAllProductImages = (prod) => {
    const images = [];
    
    const mainImageUrls = getImageUrls(prod.ImageUrl);
    if (mainImageUrls && mainImageUrls.large) {
      images.push(mainImageUrls.large);
    }

    if (prod.ProductImages && Array.isArray(prod.ProductImages) && prod.ProductImages.length > 0) {
      prod.ProductImages.forEach((img) => {
        let imgUrl = null;
        
        if (typeof img === 'object' && img !== null) {
          imgUrl = img.large || img.medium || img.thumbnail;
          if (imgUrl) {
            images.push(imgUrl);
          }
        } else if (typeof img === 'string') {
          if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:image')) {
            imgUrl = img;
          } else if (img.startsWith('/images/')) {
            imgUrl = `${config.SITE_BASE_URL}${img}`;
          } else if (img.startsWith('/')) {
            imgUrl = `${config.SITE_BASE_URL}${img}`;
          } else {
            imgUrl = `${config.SITE_BASE_URL}/images/${img}`;
          }
          if (imgUrl) {
            images.push(imgUrl);
          }
        }
      });
    }

    return images;
  };

  const handleNextImage = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
      
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const handlePrevImage = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const toggleAutoSlide = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        setAutoSlideInterval(null);
      } else {
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const groupedCategories = useMemo(() => 
    categories.filter(cat => /^مجموعة:/i.test(cat.CategoryName)),
    [categories]
  );
  
  const allGroupedProducts = useMemo(() => 
    groupedCategories.flatMap(cat =>
      products.filter(p => p.CategoryId === cat.Id).map(prod => ({
        ...prod,
        CategoryName: cat.CategoryName
      }))
    ),
    [groupedCategories, products]
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 10, sm: 12, md: 16 }, mb: 20, px: { xs: 1.5, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width={300} height={60} sx={{ mx: 'auto', mb: 4 }} />
        <Grid container spacing={{ xs: 1, sm: 2.5, md: 3 }} sx={{ justifyContent: 'center' }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid item key={i}>
              <Skeleton variant="rectangular" width={isMobile ? 140 : 220} height={isMobile ? 280 : 400} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (allGroupedProducts.length === 0) return null;

  return (
    <>
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 10, sm: 12, md: 16 },
          mb: { xs: 4, sm: 6 },
          px: { xs: 1, sm: 3, md: 4 }
        }}
      >
        {/* Header Section */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: { xs: 2.5, sm: 4, md: 5 }, 
            position: 'relative' 
          }}
        >
          <Box sx={{ position: 'relative', textAlign: 'center', px: { xs: 1.5, sm: 5 }, py: { xs: 0.8, sm: 2 } }}>
            <Typography 
              variant="h4" 
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 800,
                fontSize: { xs: '1.25rem', sm: '2rem', md: '2.25rem' },
                letterSpacing: '0.5px',
                textTransform: 'capitalize',
                position: 'relative',
                display: 'inline-block',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.error.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: { xs: -4, sm: -8 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: { xs: '35px', sm: '60px' },
                  height: { xs: '2.5px', sm: '4px' },
                  borderRadius: '2px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`
                }
              }}
            >
              {t('offers.specialOffers')}
            </Typography>
          </Box>
        </Box>

        {/* Products Grid */}
        <Grid 
          container 
          spacing={{ xs: 1, sm: 2.5, md: 3 }} 
          sx={{ 
            direction: 'rtl', 
            justifyContent: 'center' 
          }}
        >
          {allGroupedProducts.map((prod, prodIndex) => {
            const imageUrls = getImageUrls(prod.ImageUrl);
            const currentPrice = getPriceInCurrency(prod, selectedCurrency);
            const currencySymbol = getCurrencySymbol(selectedCurrency);
            const decimals = getCurrencyDecimals(selectedCurrency);
            
            let oldPrice = 0;
            let hasDiscount = false;
            
            if (prod.OldPrice) {
              if (prod.OldCurrencyPrices) {
                oldPrice = getPriceInCurrency(
                  { ...prod, CurrencyPrices: prod.OldCurrencyPrices },
                  selectedCurrency
                );
              } else {
                const priceRatio = prod.OldPrice / (prod.Price || 1);
                oldPrice = currentPrice * priceRatio;
              }
              hasDiscount = oldPrice > currentPrice;
            }
            
            const discountPercent = hasDiscount ? calculateDiscountPercentage(oldPrice, currentPrice) : 0;
            const isFavorite = favoriteIds.includes(prod.Id);

            return (
              <Grid 
                item 
                key={prod.Id}
                id={`product-${prod.Id}`}
                sx={{
                  animation: `${slideIn} 0.5s ease-out`,
                  animationDelay: `${prodIndex * 0.05}s`,
                  animationFillMode: 'backwards'
                }}
              >
                <Card 
                  sx={{ 
                    width: { xs: 140, sm: 200, md: 220 },
                    height: '100%',
                    borderRadius: { xs: 1.5, sm: 2 },
                    overflow: 'visible',
                    boxShadow: `0 ${isMobile ? '3px 10px' : '8px 24px'} ${alpha(theme.palette.common.black, 0.08)}`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    bgcolor: 'background.paper',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&:hover': { 
                      transform: isMobile ? 'scale(0.98)' : 'translateY(-12px)',
                      boxShadow: `0 ${isMobile ? '5px 14px' : '20px 40px'} ${alpha(theme.palette.primary.main, 0.2)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      '& .product-image': { transform: 'scale(1.1)' }
                    },
                    '&:active': {
                      transform: isMobile ? 'scale(0.95)' : 'translateY(-12px)',
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleProductClick(prod)}
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}
                  >
                    {/* Image Section */}
                    <Box 
                      sx={{ 
                        position: 'relative', 
                        width: '100%', 
                        height: { xs: 100, sm: 160, md: 180 },
                        overflow: 'hidden',
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        borderRadius: '12px 12px 0 0'
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={imageUrls.medium}
                        srcSet={`${imageUrls.thumbnail} 150w, ${imageUrls.medium} 400w, ${imageUrls.large} 1200w`}
                        sizes={isMobile ? "140px" : "(max-width: 960px) 200px, 220px"}
                        alt={prod.ProductName}
                        loading="lazy"
                        className="product-image"
                        sx={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover', 
                          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          cursor: 'pointer'
                        }}
                        onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                      />

                      {/* Favorite Button */}
                      <Tooltip title={isFavorite ? t('favorites.remove') : t('favorites.add')} arrow placement="left">
                        <IconButton
                          onClick={(e) => toggleFavorite(prod.Id, prod.ProductName, e)}
                          sx={{
                            position: 'absolute',
                            top: { xs: 4, sm: 10 },
                            right: { xs: 4, sm: 10 },
                            bgcolor: 'white',
                            boxShadow: { xs: '0 2px 6px rgba(0,0,0,0.15)', sm: '0 4px 12px rgba(0,0,0,0.15)' },
                            zIndex: 3,
                            width: { xs: 28, sm: 40 },
                            height: { xs: 28, sm: 40 },
                            transition: 'all 0.3s ease',
                            border: `${isMobile ? '1.5px' : '2px'} solid ${isFavorite ? theme.palette.error.main : alpha(theme.palette.text.secondary, 0.2)}`,
                            '&:hover': {
                              bgcolor: 'white',
                              transform: 'scale(1.15)',
                              boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                              borderColor: theme.palette.error.main
                            },
                            '&:active': {
                              transform: 'scale(0.95)',
                            }
                          }}
                        >
                          {isFavorite ? (
                            <FavoriteIcon sx={{ fontSize: { xs: 16, sm: 22 }, color: theme.palette.error.main, animation: `${heartBeat} 0.3s ease` }} />
                          ) : (
                            <FavoriteBorderIcon sx={{ fontSize: { xs: 16, sm: 22 }, color: theme.palette.text.secondary }} />
                          )}
                        </IconButton>
                      </Tooltip>

                      {/* Discount & Group Badges */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: 4, sm: 10 },
                          left: { xs: 4, sm: 10 },
                          display: 'flex',
                          flexDirection: 'column',
                          gap: { xs: 0.5, sm: 1 },
                          zIndex: 2
                        }}
                      >
                        {hasDiscount && (
                          <Chip
                            icon={<LocalOfferIcon sx={{ fontSize: { xs: 9, sm: 14 }, color: 'white !important' }} />}
                            label={`${discountPercent}%`}
                            sx={{
                              bgcolor: 'error.main',
                              color: 'white',
                              fontWeight: 800,
                              fontSize: { xs: '9px', sm: '12px' },
                              height: { xs: 20, sm: 28 },
                              boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.error.main, 0.4)}`,
                              animation: `${pulse} 2s infinite`,
                              '& .MuiChip-icon': {
                                color: 'white',
                                marginLeft: { xs: '2px', sm: '4px' }
                              }
                            }}
                          />
                        )}
                        <Chip
                          label={t('groupedProducts.groupBadge', 'Bundle')}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: { xs: '8px', sm: '11px' },
                            height: { xs: 18, sm: 24 },
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Content Section */}
                    <CardContent sx={{ p: { xs: 1, sm: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Tooltip title={prod.ProductName} arrow>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontSize: { xs: '11px', sm: '15px', md: '16px' },
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: { xs: 0.2, sm: 0.5 },
                            textAlign: 'center',
                            lineHeight: 1.3,
                            height: { xs: '2.3em', sm: '2.6em' },
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {prod.ProductName}
                        </Typography>
                      </Tooltip>

                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: { xs: '8px', sm: '11px' },
                          color: 'text.secondary',
                          mb: { xs: 0.8, sm: 1.5 },
                          textAlign: 'center',
                          lineHeight: 1.3,
                          height: { xs: '2.3em', sm: '2.8em' },
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {prod.Description || t('groupedProducts.noDescription', 'No description')}
                      </Typography>

                      {prod.Ingredients && prod.Ingredients.length > 0 && (
                        <Box 
                          sx={{ 
                            mb: { xs: 0.8, sm: 1.5 },
                            p: { xs: 0.5, sm: 1 },
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            borderRadius: { xs: 1, sm: 2 },
                            border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, sm: 0.5 }, mb: { xs: 0.2, sm: 0.3 }, justifyContent: 'center' }}>
                            <InfoOutlinedIcon sx={{ fontSize: { xs: 9, sm: 12 }, color: 'primary.main' }} />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme.palette.primary.main,
                                fontWeight: 700,
                                fontSize: { xs: '7px', sm: '10px' },
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                              }}
                            >
                              {t('products.ingredients', 'Ingredients')}
                            </Typography>
                          </Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: { xs: '7px', sm: '10px' },
                              textAlign: 'center',
                              display: 'block',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {Array.isArray(prod.Ingredients) ? prod.Ingredients.join(', ') : prod.Ingredients}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ flexGrow: 1 }} />

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.4, sm: 1 }, mb: { xs: 1, sm: 2 } }}>
                        <Typography sx={{ color: theme.palette.primary.main, fontWeight: 800, fontSize: { xs: '12px', sm: '18px' } }}>
                          {currentPrice.toFixed(decimals)} {currencySymbol}
                        </Typography>
                        {hasDiscount && (
                          <Typography sx={{ textDecoration: 'line-through', color: theme.palette.text.disabled, fontSize: { xs: '9px', sm: '12px' }, fontWeight: 500 }}>
                            {oldPrice.toFixed(decimals)} {currencySymbol}
                          </Typography>
                        )}
                      </Box>

                      {/* Action Button: تسوق الان opening https://www.elorayem.com/product/{PRODUCT ID} in new tab */}
                      <Box sx={{ width: '100%', mt: 'auto' }}>
                        {prod.IsActive === false ? (
                          <Box 
                            sx={{
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              border: `${isMobile ? '1.5px' : '2px'} solid ${alpha(theme.palette.error.main, 0.3)}`,
                              borderRadius: { xs: 1.5, sm: 3 },
                              py: { xs: 0.8, sm: 1.5 },
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Typography sx={{ color: theme.palette.error.main, fontWeight: 700, fontSize: { xs: '9px', sm: '13px' }, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                              {t('home.outOfStock', 'Out of Stock')}
                            </Typography>
                          </Box>
                        ) : (
                          <Button 
                            variant="contained"
                            fullWidth
                            onClick={(e) => handleOpenExternalProduct(prod, e)}
                            sx={{ 
                              bgcolor: theme.palette.primary.main,
                              borderRadius: { xs: 1.5, sm: 3 },
                              color: theme.palette.primary.contrastText,
                              py: { xs: 0.7, sm: 1.2 },
                              fontSize: { xs: '9px', sm: '13px' },
                              fontWeight: 700,
                              textTransform: 'none',
                              boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.primary.main, 0.3)}`,
                              '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                                boxShadow: `0 ${isMobile ? '3px 10px' : '6px 16px'} ${alpha(theme.palette.primary.main, 0.4)}`,
                                transform: isMobile ? 'none' : 'translateY(-2px)'
                              },
                              '&:active': { transform: 'scale(0.95)' },
                              transition: 'all 0.3s ease',
                              gap: { xs: 0.4, sm: 1 }
                            }}
                          >
                            <OpenInNewIcon sx={{ fontSize: { xs: 12, sm: 18 } }} />
                            تسوق الان
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Product Details Modal */}
      <Modal
        open={!!selectedProduct}
        onClose={handleCloseProductModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: alpha(theme.palette.common.black, 0.75)
          }
        }}
      >
        <Fade in={!!selectedProduct}>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: '850px' },
              maxHeight: { xs: '80vh', sm: '82vh', md: '85vh' },
              mt: { xs: 6, sm: 7, md: 8 },
              mb: { xs: 6, sm: 7, md: 8 },
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: { xs: 2, sm: 4 },
              boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.5)}`,
              outline: 'none',
              animation: `${slideIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1)`,
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: alpha(theme.palette.divider, 0.1),
                borderRadius: '10px'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(theme.palette.primary.main, 0.3),
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.5)
                }
              }
            }}
          >
            {selectedProduct && (() => {
              const images = getAllProductImages(selectedProduct);
              const currentPrice = getPriceInCurrency(selectedProduct, selectedCurrency);
              const currencySymbol = getCurrencySymbol(selectedCurrency);
              const decimals = getCurrencyDecimals(selectedCurrency);
              
              let oldPrice = 0;
              let hasDiscount = false;
              
              if (selectedProduct.OldPrice) {
                if (selectedProduct.OldCurrencyPrices) {
                  oldPrice = getPriceInCurrency(
                    { ...selectedProduct, CurrencyPrices: selectedProduct.OldCurrencyPrices },
                    selectedCurrency
                  );
                } else {
                  const priceRatio = selectedProduct.OldPrice / (selectedProduct.Price || 1);
                  oldPrice = currentPrice * priceRatio;
                }
                hasDiscount = oldPrice > currentPrice;
              }
              
              const discountPercent = hasDiscount ? calculateDiscountPercentage(oldPrice, currentPrice) : 0;
              const isFavorite = favoriteIds.includes(selectedProduct.Id || selectedProduct.id);

              return (
                <>
                  <IconButton
                    onClick={handleCloseProductModal}
                    sx={{
                      position: 'absolute',
                      top: { xs: 8, sm: 16 },
                      right: { xs: 8, sm: 16 },
                      bgcolor: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(10px)',
                      zIndex: 10,
                      boxShadow: 3,
                      width: { xs: 36, sm: 44 },
                      height: { xs: 36, sm: 44 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                        transform: 'rotate(90deg) scale(1.1)',
                        boxShadow: 6
                      },
                      '&:active': {
                        transform: 'scale(0.9)'
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </IconButton>

                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ position: 'relative', width: '100%' }}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: { xs: 300, sm: 400, md: 450 },
                            borderRadius: 3,
                            overflow: 'hidden',
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                            border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`
                          }}
                        >
                          {images.length > 0 ? (
                            <Box
                              component="img"
                              src={images[currentImageIndex]}
                              alt={`${selectedProduct.ProductName} - Image ${currentImageIndex + 1}`}
                              onClick={(e) => handleImageClick(images[currentImageIndex], e)}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                cursor: 'zoom-in',
                                transition: 'opacity 0.6s ease-in-out, transform 0.3s ease',
                                animation: `${fadeIn} 0.6s ease-in-out`,
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                              onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                              key={`image-${currentImageIndex}`}
                            />
                          ) : (
                            <Box
                              component="img"
                              src="/logo-storesye.png"
                              alt="No image available"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          )}

                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              right: 16,
                              bgcolor: alpha(theme.palette.common.black, 0.6),
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backdropFilter: 'blur(10px)',
                              cursor: 'zoom-in',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.common.black, 0.8),
                                transform: 'scale(1.05)'
                              }
                            }}
                            onClick={(e) => images.length > 0 && handleImageClick(images[currentImageIndex], e)}
                          >
                            <ZoomInIcon sx={{ fontSize: 18, color: 'white' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'white', fontWeight: 600 }}>
                              {t('products.clickToZoom', 'Click to Zoom')}
                            </Typography>
                          </Box>

                          {images.length > 1 && (
                            <Tooltip 
                              title={autoSlideInterval ? t('products.pauseSlideshow', 'Pause') : t('products.playSlideshow', 'Play')} 
                              arrow
                            >
                              <IconButton
                                onClick={toggleAutoSlide}
                                sx={{
                                  position: 'absolute',
                                  bottom: 16,
                                  left: 16,
                                  bgcolor: alpha(theme.palette.common.black, 0.6),
                                  backdropFilter: 'blur(10px)',
                                  color: 'white',
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.common.black, 0.8),
                                    transform: 'scale(1.1)'
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                              >
                                {autoSlideInterval ? (
                                  <PauseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                ) : (
                                  <PlayArrowIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}

                          {images.length > 1 && (
                            <>
                              <IconButton
                                onClick={handlePrevImage}
                                sx={{
                                  position: 'absolute',
                                  left: { xs: 8, sm: 16 },
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                                  backdropFilter: 'blur(10px)',
                                  boxShadow: 3,
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  '&:hover': {
                                    bgcolor: theme.palette.background.paper,
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: 6
                                  },
                                  '&:active': {
                                    transform: 'translateY(-50%) scale(0.95)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <NavigateBeforeIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                              </IconButton>
                              <IconButton
                                onClick={handleNextImage}
                                sx={{
                                  position: 'absolute',
                                  right: { xs: 8, sm: 16 },
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                                  backdropFilter: 'blur(10px)',
                                  boxShadow: 3,
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  '&:hover': {
                                    bgcolor: theme.palette.background.paper,
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: 6
                                  },
                                  '&:active': {
                                    transform: 'translateY(-50%) scale(0.95)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <NavigateNextIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                              </IconButton>
                            </>
                          )}

                          {hasDiscount && (
                            <Chip
                              icon={<LocalOfferIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                              label={`${discountPercent}% ${t('products.off', 'OFF')}`}
                              sx={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                bgcolor: theme.palette.error.main,
                                color: 'white',
                                fontWeight: 800,
                                fontSize: '14px',
                                height: 32,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`,
                                zIndex: 2,
                                '& .MuiChip-icon': {
                                  color: 'white'
                                }
                              }}
                            />
                          )}

                          {images.length > 1 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                bgcolor: alpha(theme.palette.common.black, 0.6),
                                borderRadius: 2,
                                px: 1.5,
                                py: 0.5,
                                backdropFilter: 'blur(10px)'
                              }}
                            >
                              <Typography sx={{ fontSize: '0.75rem', color: 'white', fontWeight: 600 }}>
                                {currentImageIndex + 1} / {images.length}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {images.length > 1 && (
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              mt: 2,
                              overflowX: 'auto',
                              pb: 1,
                              '&::-webkit-scrollbar': {
                                height: '6px'
                              },
                              '&::-webkit-scrollbar-track': {
                                bgcolor: alpha(theme.palette.divider, 0.1),
                                borderRadius: '10px'
                              },
                              '&::-webkit-scrollbar-thumb': {
                                bgcolor: alpha(theme.palette.primary.main, 0.3),
                                borderRadius: '10px',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.5)
                                }
                              }
                            }}
                          >
                            {images.map((img, index) => (
                              <Box
                                key={`thumb-${index}`}
                                onClick={() => {
                                  setCurrentImageIndex(index);
                                  if (autoSlideInterval) {
                                    clearInterval(autoSlideInterval);
                                    const newInterval = setInterval(() => {
                                      setCurrentImageIndex((prev) => (prev + 1) % images.length);
                                    }, 3000);
                                    setAutoSlideInterval(newInterval);
                                  }
                                }}
                                sx={{
                                  minWidth: { xs: 60, sm: 80 },
                                  height: { xs: 60, sm: 80 },
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  border: `3px solid ${index === currentImageIndex ? theme.palette.primary.main : 'transparent'}`,
                                  opacity: index === currentImageIndex ? 1 : 0.6,
                                  transition: 'all 0.3s ease',
                                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                                  position: 'relative',
                                  '&:hover': {
                                    opacity: 1,
                                    transform: 'scale(1.05)',
                                    boxShadow: 3
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                              >
                                <Box
                                  component="img"
                                  src={img}
                                  alt={`Thumbnail ${index + 1}`}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                                />
                                {index === currentImageIndex && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      height: '3px',
                                      bgcolor: theme.palette.primary.main,
                                      animation: `${slideTransition} 0.3s ease`
                                    }}
                                  />
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Stack spacing={{ xs: 2, sm: 3 }}>
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                              color: theme.palette.text.primary,
                              mb: 1,
                              lineHeight: 1.2
                            }}
                          >
                            {selectedProduct.ProductName}
                          </Typography>

                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={categories.find(c => c.Id === selectedProduct.CategoryId)?.CategoryName || t('products.category', 'Category')}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                fontSize: '0.8rem'
                              }}
                            />
                            <Chip
                              label={t('groupedProducts.groupBadge', 'Bundle')}
                              size="small"
                              sx={{
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.8rem'
                              }}
                            />
                          </Stack>
                        </Box>

                        <Divider />

                        <Box>
                          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 900,
                                color: theme.palette.primary.main,
                                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' }
                              }}
                            >
                              {currentPrice.toFixed(decimals)} {currencySymbol}
                            </Typography>
                            {hasDiscount && (
                              <Typography
                                variant="h5"
                                sx={{
                                  textDecoration: 'line-through',
                                  color: theme.palette.text.disabled,
                                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                                  fontWeight: 600
                                }}
                              >
                                {oldPrice.toFixed(decimals)} {currencySymbol}
                              </Typography>
                            )}
                          </Stack>
                          {hasDiscount && (
                            <Typography
                              sx={{
                                color: theme.palette.success.main,
                                fontWeight: 700,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }}
                            >
                              {t('products.youSave', 'You save')} {(oldPrice - currentPrice).toFixed(decimals)} {currencySymbol} ({discountPercent}%)
                            </Typography>
                          )}
                        </Box>

                        <Divider />

                        {selectedProduct.Description && (
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 1,
                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                color: theme.palette.text.primary
                              }}
                            >
                              {t('products.description', 'Description')}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.8,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }}
                            >
                              {selectedProduct.Description}
                            </Typography>
                          </Box>
                        )}

                        {selectedProduct.Ingredients && selectedProduct.Ingredients.length > 0 && (
                          <Box
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              borderRadius: 2,
                              border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                  color: theme.palette.primary.main
                                }}
                              >
                                {t('products.ingredients', 'Ingredients')}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.6,
                                fontSize: { xs: '0.85rem', sm: '0.95rem' }
                              }}
                            >
                              {Array.isArray(selectedProduct.Ingredients) 
                                ? selectedProduct.Ingredients.join(', ') 
                                : selectedProduct.Ingredients}
                            </Typography>
                          </Box>
                        )}

                        <Divider />

                        {/* Action Buttons */}
                        <Stack spacing={2}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => toggleFavorite(selectedProduct.Id || selectedProduct.id, selectedProduct.ProductName)}
                            startIcon={
                              isFavorite ? (
                                <FavoriteIcon sx={{ color: theme.palette.error.main }} />
                              ) : (
                                <FavoriteBorderIcon />
                              )
                            }
                            sx={{
                              borderRadius: 3,
                              py: { xs: 1.2, sm: 1.5 },
                              fontSize: { xs: '0.95rem', sm: '1rem' },
                              fontWeight: 700,
                              borderWidth: 2,
                              borderColor: isFavorite ? theme.palette.error.main : theme.palette.divider,
                              color: isFavorite ? theme.palette.error.main : theme.palette.text.primary,
                              '&:hover': {
                                borderWidth: 2,
                                borderColor: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.05),
                                transform: 'translateY(-2px)',
                                boxShadow: 3
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {isFavorite ? t('favorites.removeFromFavorites', 'Remove from Favorites') : t('favorites.addToFavorites', 'Add to Favorites')}
                          </Button>

                          {/* تسوق الان Button in modal */}
                          {selectedProduct.IsActive === false ? (
                            <Box
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                borderRadius: 3,
                                py: { xs: 1.5, sm: 2 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Typography
                                sx={{
                                  color: theme.palette.error.main,
                                  fontWeight: 700,
                                  fontSize: { xs: '1rem', sm: '1.15rem' },
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                {t('home.outOfStock', 'Out of Stock')}
                              </Typography>
                            </Box>
                          ) : (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={(e) => handleOpenExternalProduct(selectedProduct, e)}
                              startIcon={<OpenInNewIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                              sx={{
                                bgcolor: theme.palette.primary.main,
                                borderRadius: 3,
                                py: { xs: 1.5, sm: 2 },
                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                fontWeight: 700,
                                textTransform: 'none',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                                '&:hover': {
                                  bgcolor: theme.palette.primary.dark,
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`
                                },
                                '&:active': {
                                  transform: 'scale(0.98)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              تسوق الان
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </>
              );
            })()}
          </Box>
        </Fade>
      </Modal>

      {/* Image Zoom Modal */}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: alpha(theme.palette.common.black, 0.85)
          }
        }}
      >
        <Fade in={!!selectedImage}>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '95vw',
              maxHeight: { xs: '80vh', sm: '82vh', md: '85vh' },
              mt: { xs: 6, sm: 7, md: 8 },
              mb: { xs: 6, sm: 7, md: 8 },
              outline: 'none',
              borderRadius: { xs: 2, sm: 4 },
              overflow: 'hidden',
              boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.5)}`,
              animation: `${slideIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1)`
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                bgcolor: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(10px)',
                zIndex: 1,
                boxShadow: 3,
                width: { xs: 32, sm: 44 },
                height: { xs: 32, sm: 44 },
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: theme.palette.background.paper,
                  transform: 'rotate(90deg) scale(1.1)'
                },
                '&:active': {
                  transform: 'scale(0.9)'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </IconButton>
            <Box
              component="img"
              src={selectedImage}
              alt="Product"
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: { xs: '80vh', sm: '82vh', md: '85vh' },
                objectFit: 'contain',
                borderRadius: { xs: 2, sm: 4 }
              }}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: 4,
            minWidth: { xs: 200, sm: 300 },
            fontSize: { xs: '0.8rem', sm: '0.95rem' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default GroupedProducts;
